import urlJoin from "url-join";
import objectAssignDeep from "./tools/object-assign-deep";
import { v4 as uuid } from "uuid";
import qrCode from "qrcode-generator";
import MessageBox from "./messagebox";
import HcsProgressbar from "./controls/hcs-progressbar";
import "css.escape";
import LZString from "lz-string";
import html2canvas from "html2canvas";
import he from "he";
import RatingControl from "./controls/hcs-rating-control";
import { TrackingTypes } from "./tools/tracking-base";

/**
 * @class
 * @classdesc Main API class.
 */
export default class HcsApi {
    constructor(paramObj) {
        let self = this;

        this.hcs = paramObj.hcs;

        // We use CSS.escape to allow numerical container IDs
        this.container = document.querySelector("#" + CSS.escape(this.hcs.container.id));

        this.configTriggerEvents = [];
        this.ecsTriggerEvents = [];
        this.configurationResetEvents = [];
        this.subconfigurationChangedEvents = [];
        this.printTriggerEvents = [];

        this.messageBox = new MessageBox({
            hcs: this.hcs,
            selector: self.instance(".hcs-msg-box")
        });

        this.progressBar = new HcsProgressbar({
            hcs: Object.assign({},
                this.hcs, {
                    api: this
            }),
            container: this.instance()
        });

        // Store common session settings.
        // To set or read values see the session() function.
        this._sessionData = {};
    }

    get ratingControl() {
        if(!this._ratingControl) {
            this._ratingControl = new RatingControl({
                hcs: this.hcs
            });
        }

        return this._ratingControl;
    }

    /**
     * Select elements inside main configurator container.
     * @param {string} [selector] - CSS selector to select elements. Leave empty to get the main container.
     * @param {HTMLElement} [container] - DOM element which acts as container where item selection begins. If not specified we
     * use the most outer HCS  element.
     * @returns {HTMLElement|Array} DOM element(s) for given selector.
     * Returns a single DOM element when selector matches excatly one item, otherwise an array of DOM elements.
     */
    instance(selector, container = this.hcs.container) {
        if(arguments.length > 0 && typeof (selector) !== "undefined") {
            let selResults = container.querySelectorAll(selector);

            if(selResults !== null && selResults.length === 1) {
                return selResults[0];
            } else {
                if(selResults.length > 0) {
                    return selResults;
                } else {
                    return null;
                }
            }
        }

        return container;
    }

    /**
     * Build basic URL for requests.
     * The URL contains the base part combined with the name of the configurator as default.
     * It can be extended by passing one or more arguments.
     * When the configurator name extension is not required set the first function argument to "false".
     * 
     * @example
     * Base URL is: http://configurator.cadclick.com
     * Configurator name is: dress_code
     * 
     * getUrl("part1", "part2") returns http://configurator.cadclick.com/dress_code/part1/part2
     * getUrl(false, "part1", "part2") returns http://configurator.cadclick.com/part1/part2
     * 
     * @param {boolean} [includeConfigurator=true] - As default the configurator name will be automatically added.
     * When not required set first argument to "false".
     * @param {...*} [args] - List of URL parts which will be added to base URL.
     * @returns {string} Bsae URL combined with custom parts.
     */
    getUrl(includeConfigurator, ...args) {
        let parts = [].slice.call(arguments).filter((_item, index) => {
            return typeof (includeConfigurator) === "string" ? true : index > 0;
        });

        return urlJoin([this.hcs.constants.appUrl, (typeof (includeConfigurator) === "string" || includeConfigurator === true) ? this.hcs.configurator : ""].concat(parts));
    }

    /**
     * Display a modal dialog displaying an error message.
     * 
     * @param {Object} parameters - List of parameters
     * @param {string} parameters.title - Text for dialog title.
     * @param {string|Object} parameters.messageMap - This is how the message object is being mapped to the dialog text.
     * The standard error object must contain a "message" and "details" (optional) string.
     * For error objects from within a catch block use "jsError".
     * For all other (custom) objects you can define your own mapping profile.
     * @param {Object} message - Error message object. The object structure must match the type as defined above.
     * @param {boolean} [includeSessionDetails=true] - Show additional session properties like culture, section and configuration ID.
     * @param {boolean} [storeLog=true] - Write error message to 
     */
    showError(parameters) {
        let self = this;

        /*
        
        window.cadclick.hcs["dress_code"].api.showError({
            "title": "Error in HCS renderer",
            "messageMap": "jsError",
            "message": {
                "message": "Failed to fetch",
                "stack": "TypeError: Failed to fetch\n    at HcsConfigurator.configure (http://localhost/HAEFELE_CONFIGURATIONSUITE_DE/api/hcs?callback=hcsSuccess:74850:7)\n    at http://localhost/HAEFELE_CONFIGURATIONSUITE_DE/api/hcs?callback=hcsSuccess:69255:31",
                "messageType": "jsError"
            }
        })

         */

        var params = Object.assign({}, {
            title: null,
            messageMap: [
                {
                    name: "message",
                    label: "",
                    className: "hcs-error-message"
                },
                {
                    name: "url",
                    label: "URL:"
                }/*,
                {
                    name: "details",
                    label: "",
                    className: "hcs-error-message hcs-error-details"
                }*/
            ],
            message: null,
            includeSessionDetails: true,
            storeLog: true
        }, parameters);

        if(this.progressBar) {
            this.progressBar.progress(false);
        }

        if(!params.message) {
            console.error("Could not display error message dialog: No 'message' object defined!");
            return;
        }

        switch(params.messageMap) {
            case "jsError":
                params.messageMap = [
                    {
                        name: "message",
                        className: "hcs-error-message"
                    },
                    {
                        name: "url",
                        label: "URL:"
                    },
                    {
                        name: "fileName",
                        label: "Filename:"
                    },
                    {
                        name: "lineNumber",
                        label: "Line:"
                    },
                    {
                        name: "columnNumber",
                        label: "Column:"
                    }/*,
                    {
                        name: "stack",
                        label: "Stack:"
                    }*/
                ];
                break;
        }

        let errorText = "";
        let errorTextLogger = "";
        let showErrorImage = this.hcs.cfgEngine.errorDialogImageName();

        params.messageMap.forEach((item) => {
            if(params.message[item.name]) {
                errorText += `<div${item.className ? ' class="' + item.className + '"' : ''}>${(item.label ? "<label>" + item.label + "</label>" : "") + he.escape(params.message[item.name] || "-")}</div>`;
                errorTextLogger += `${(item.label ? item.label + ": " : "") + he.escape(params.message[item.name] || "-")}`;
            }
        });

        if(params.includeSessionDetails) {
            let sessionData = this.getSessionData();

            errorTextLogger += `\nSession details:\n` + sessionData.map((i) => {
                return `${i.label} ${i.value}`;
            }).join("\n");
        }

        let showMessageFunc = (errorText, eventId) => {
            if(params.includeSessionDetails) {
                let sessionData = this.getSessionData();

                if(eventId) {
                    sessionData.push({
                        id: "eventid", label: "Event ID:", value: eventId
                    });
                }

                errorText += `<fieldset class="hcs-error-session-details"><legend>Session details</legend>` +
                    sessionData
                    .filter(i => ["timestamp", "eventid"].includes(i.id))
                        .map(i => {
                            return `<div><label>${i.label}</label>${i.value}</div>`;
                        }).join("") + `</fieldset>`;
            }

            console.error(errorTextLogger);

            let errorPanelHtml = `<div class="hcs-error-panel">` +
                `<div class="hcs-title-slogan">${this.hcs.t.gui("ErrorMessageDialog_TitleSlogan")}</div>` +
                `<div class="hcs-error-image">` + (showErrorImage ? `<div class="hcs-image-placeholder"><img src="${ this.getUrl(false, "ProjectFiles", this.hcs.configurator, "images/error-image.png") }" /></div>` : "") + `</div>` +
                `<div class="hcs-error-message">${ errorText }</div>` +
            `</div>`;

            this.messageBox.show({
                modal: true,
                title: this.hcs.t.gui("ErrorMessageDialog_Title"),
                text: errorPanelHtml.replace(/(\r\n|\n|\r)/gm, "<br />"),
                titleCloseButton: true,
                buttons: MessageBox.MB_OK | MessageBox.MB_CANCEL,
                buttonTexts: {
                    "ok": this.hcs.t.gui("ErrorMessageDialog_ButtonReloadPage"),
                    "cancel": this.hcs.t.gui("Cancel")
                },
                buttonClassCategories: {
                    "ok": "hcs-secondary",
                    "cancel": "hcs-tertiary"
                },
                messageType: MessageBox.MB_ERROR,
                onOk: function(event) {
                    window.location.href = window.location.href;
                }
            });
        };

        if(params.storeLog) {
            try {
                this.hcs.api.callback({
                    url: this.hcs.api.getUrl(`log/error/${this.hcs.configurationId}`),
                    data: {
                        error: errorTextLogger
                    },
                    method: "POST"
                }).then(result => {
                    if(result.success) {
                        showMessageFunc.call(self, errorText, result.eventId);
                    }
                }).catch(_ => {
                    showMessageFunc.call(self, errorText);
                });
            } catch(e) {
                console.error(e);
                showMessageFunc.call(self, errorText);
            }
        } else {
            showMessageFunc.call(self, errorText);
        }
    }

    getSessionData() {
        return [
            { id: "configurator", label: "Configurator:", value: this.hcs.configurator },
            { id: "configurationid", label: "Configuration ID:", value: this.hcs.configurationId },
            { id: "userid", label: "User ID:", value: this.hcs.userId },
            { id: "sectionid", label: "Section ID:", value: this.hcs.sectionId },
            { id: "cultureid", label: "Culture ID:", value: this.hcs.cultureId },
            { id: "ecssessionid", label: "ECS section ID:", value: this.hcs.ecsSectionId },
            { id: "url", label: "URL:", value: window.location.href },
            { id: "browser", label: "Browser:", value: this.hcs.ui.browser.browser.name + " " + this.hcs.ui.browser.browser.version + " (" + this.hcs.ui.browser.platform.type + ")" },
            { id: "timestamp", label: "Timestamp:", value: new Date().toLocaleString() }
        ];
    }

    /**
     * Most AJAX calls always have the same structure and parameters.
     * "Callback" is doing a standard AJAX call with default parameters.
     * @param {object} params - Callback options
     * @param {string} params.url - URL to be called.
     * @param {string} [params.method=GET] - Either "GET" or "POST"
     * @param {bool} [params.cache=no-cache] - Caching options.
     * @param {object} [params.data] - Data object for POST data. Requires that method is set to "POST".
     * @param {bool} [params.showErrorMessage=true] - When true automatically shows an error message dialog. In case of false you have to handle errors manually.
     * @param {bool} [params.lockUi] - When true lock complete UI with indeterminate spinner.
     * @param {function} [params.success] - Function being called after successful request.
     * @param {function} [params.error] - Function being called when request has failed.
     * @param {function} [params.checkSuccess] - Function to check whether the request was successful dpending on the response data.
     * Leave empty when the resulting JSON object has a property: success=true. In all other cases override this function and evaluate the success state by hand.
     */
    callback(params) {
        var self = this;
        var loading = true;

        var options = Object.assign({}, {
            method: "GET",
            cache: "no-cache",
            data: {},
            showErrorMessage: true,
            lockUi: false,
            checkSuccess: (result) => result.success
        }, params);

        if(options.lockUi) {
            setTimeout(function() {
                if(loading) {
                    self.progressBar.progress("indeterminate");
                }
            }, 200);
        }

        return new Promise((resolve, reject) => {
            fetch(options.url, {
                method: options.method,
                cache: options.cache,
                body: options.method === "GET" ? undefined : JSON.stringify(options.data),
                headers: {
                    "Content-Type": options.method === "POST" ? "application/json;charset=UTF-8" : undefined
                }
            })
                .then(res => res.json())
                .then((result) => {
                    if(options.lockUi) {
                        loading = false;
                        self.progressBar.progress(false);
                    }

                    resolve(result);

                    if(!options.checkSuccess(result)) {
                        if(options.showErrorMessage) {
                            self.showError({
                                title: "Error in callback '" + options.url + "'",
                                message: result.error
                            });
                        }
                    }
                }).catch((error) => {
                    if(options.lockUi) {
                        loading = false;
                        self.progressBar.progress(false);
                    }

                    reject(error);

                    if(options.showErrorMessage) {
                        self.showError({
                            title: "Error in Callback",
                            message: {
                                message: "URL: " + options.url + "<hr />" + error.message
                            }
                        });
                    }
                });
        });
    }

    /**
     * Build ECS request URL
     * @param {object} options Additional call options:
     *   - method: ECS method like "startConfiguration", "setCfgValue", ...
     *   - params: Additional parameters being send to ECS as key/value pair:
     *         { "param1": "value1" }
     *         { "param2": "value2" }
     *         ....
     * @returns {string} Request URL to be sent to ECS proxy.
     */
    ecs(options) {
        var opts = objectAssignDeep({}, {
            method: "",
            params: {
                "configurator": this.hcs.configurator,
                "configurationId": this.hcs.configurationId,
                "sectionid": this.hcs.sectionId,
                "cultureid": this.hcs.cultureId,
                "userId": this.hcs.userId,
                "ajxce": this.ajaxBodyCompressionEnabled ? "1" : "0"
            }
        }, options);

        var url = this.getUrl("/proxy/ecs/" + opts.method);

        Object.keys(opts.params).filter((key) => { return typeof opts.params[key] !== "undefined" && opts.params[key] !== null; }).forEach(function(key, index) {
            url += (index > 0 ? "&" : "?") + key + "=" + opts.params[key];
        });

        return url;
    }

    /**
     * Save current ccAO XML build instructions to user session.
     * @param {Object} options - Options
     * @param {function} options.success - Function being called after callback has finished.
     */
    saveBuildInstructions(options) {
        let self = this;

        let opts = Object.assign({}, {
            includeAr: false,
            success: function() { }
        }, options);

        let biData = {
            "buildInstructions": this.hcs.cad.getBuildInstructions()
        };

        self.callback({
            url: self.ecs({ method: "saveBuildInstructions" }),
            data: this.hcs.api.ajaxCompressBody(biData),
            method: "POST"
        }).then(result => {
            opts.success.call(self, result);
        });
    }

    /**
     * Save custom option value to session history.
     * @param {string} optionName - Name or ID of the option
     * @param {string} optionValue - Option value (only strings allowed).
     * @param {Object} [options] - Options
     * @param {function} [options.success] - Function being called after callback has finished.
     */
    saveCustomOption(optionName, optionValue, options) {
        let self = this;

        let opts = Object.assign({}, {
            success: function() { }
        }, options);

        self.callback({
            url: self.ecs({ method: "saveCustomOption" }) + "&option=" + optionName,
            data: optionValue,
            method: "POST"
        }).then(result => {
            opts.success.call(self, result);
        });
    }

    /**
     * Reset existing configuration and start a new one.
     * @param {object} [options] - Options
     * @param {string} [options.configurationId] Start configuration with a self-defined configuration ID. When net specified a new ID will be obtained form shop interface.
     * @param {bool} [options.showConfirmDialog=true] When set to "true" or not specified a dialog is being displayed where user has to confirm to reset the configuration.
     * @param {bool} [options.logToolbarOption=false] When set to "true" the new configuration startupup is being logged.
     */
    startConfiguration(options) {
        var self = this;

        let opts = Object.assign({}, {
            configurationId: null,
            showConfirmDialog: true,
            logToolbarOption: false
        }, options);

        var triggerResetListeners = function(uid) {
            window.cadclick.debug.console.info("Start new session:" +
                window.cadclick.debug.console.table(self.getSessionData().map(i => {
                    let dr = {};
                    dr[i.label] = i.value;

                    return dr;
                })));

            self.configurationResetEvents.forEach((eventFunction) => {
                eventFunction.call(self, {
                    configId: uid
                });
            });
        };

        var configReset = function() {
            self.progressBar.progress("indeterminate");

            if(opts.logToolbarOption) {
                self.logToolbarOption("Start Configuration");
            }

            self.hcs.renderer.reset();

            let uidReady = function(uid) {
                self.hcs.configurationId = uid;

                // We convert the (external) configuration ID to a UID which will be accepted by ccGenerator through ccAO.
                self.hcs.cad.ccAO.Options.sessionId = uuid();
                self.hcs.cad.ccAO.Options.configId = self.hcs.cad.ccAO.Options.sessionId;

                self.hcs.cart.lock();
                self.hcs.cad.hide();

                let userData = {
                    browserName: self.hcs.ui.browser.browser.name,
                    browserVersion: self.hcs.ui.browser.browser.version,
                    platform: self.hcs.ui.browser.platform.type,
                    osName: self.hcs.ui.browser.os.name,
                    osVersion: self.hcs.ui.browser.os.version,
                    url: window.location.href
                };

                setTimeout(_ => {
                    triggerResetListeners(uid);

                    self.hcs.renderer.configure("startConfiguration", self.hcs.configurationId || null, null, {
                        jsonData: userData,
                        success: () => {
                            self.hcs.cart.setPrice(0);
                            self.configStorage("reset");

                            window.cadclick.debug.console.debug("Storage state:" +
                                window.cadclick.debug.console.table(self.configStorage("debug-info"))
                            );

                            self.hcs.cad.show();

                            self.updateHeartbeat(uid);

                            self.progressBar.progress(false);
                        }
                    });
                }, 50);
            };

            if(opts.configurationId) {
                uidReady(opts.configurationId);
            } else {
                self.createConfigurationId().then(newUid => {
                    uidReady(newUid);
                });
            }
        };

        if(opts.showConfirmDialog) {
            this.messageBox.show({
                title: this.hcs.t.gui("StartConfiguration_Dialog_Title"),
                text: this.hcs.t.gui("StartConfiguration_Dialog_Message"),
                buttons: MessageBox.MB_YES | MessageBox.MB_NO,
                messageType: MessageBox.MB_QUESTION,
                onYes: function() {
                    this.close();
                    configReset.call(self);
                }
            });
        } else {
            configReset.call(self);
        }
    }

    /**
     * Create new configuration ID.
     * @param {string} [options.suppressErrorMessage=false] - Set "true" to not show error popup when creating a new ID has failed.
     * @returns {Promise} Promise object being triggered after new ID has been created.
     */
    createConfigurationId(options) {
        let opts = Object.assign({}, {
            suppressErrorMessage: false
        }, options);

        return new Promise((resolve, reject) => {
            this.hcs.externalFunction("SETTINGS.SHOP_CONFIGURATION_START_GET_CFGID").then((response) => {
                resolve(response.uuid);
            }).catch(e => {
                var error = {
                    title: "Could not create configuration ID",
                    message: e,
                    messageMap: "jsError"
                };

                if(!opts.suppressErrorMessage) {
                    self.showError(error);
                }

                reject(error);
            });
        });
    }

    /**
     * Update heartbeat.
     * @param {string} [configurationId] - Override current configuration ID. When not set the current active ID is being used.
     */
    updateHeartbeat(configurationId) {
        this.instance(".hcs-heartbeat-container iframe").src = urlJoin([this.hcs.constants.appUrl, "HcsHeartbeat", this.hcs.configurator, configurationId || this.hcs.configurationId]) + "?v=" + new Date().getTime();
    }

    /**
     * Trigger event being automatically called when EK submits a change event (user clicks next, ....)
     * @param {any} triggerData ccAO trigger configuration data from EK.
     */
    configTrigger(triggerData) {
        this.configTriggerEvents.forEach(eventFunction => {
            eventFunction.call(this, triggerData);
        }, this);
    }

    /**
     * Trigger event being automatically after every ECS request.
     * @param {string} ecsMethod - Name of the ECS method (setCfgValue, ...)
     * @param {any} ecsData - JSON response object from ECS
     * @param {object} [options] Optional properties
     */
    ecsTrigger(ecsMethod, ecsData, options) {
        this.ecsTriggerEvents.forEach(eventFunction => {
            eventFunction.call(this, ecsMethod, ecsData, options);
        }, this);
    }

    /**
     * Trigger event when subconfiguration changes.
     * @param {string} subconfigurationId - Ident of subconfiguration.
     * @param {number} subconfigurationIndex - Index of the subconfiguration.
     */
    subconfigurationChangedTrigger(subconfigurationId, subconfigurationIndex) {
        this.subconfigurationChangedEvents.forEach(eventFunction => {
            eventFunction.call(this, subconfigurationId, subconfigurationIndex);
        }, this);
    }

    /**
     * Call to initiate print function.
     * Configuration must implement the {@link HcsApi#onPrintTrigger} event.
     */
    printTrigger() {
        this.printTriggerEvents.forEach(eventFunction => {
            eventFunction.call(this);
        }, this);
    }

    /**
     * Return ccAO build instructions as JSON string.
     * The build instructions object will be reduced to only necessary data.
     * Mesh data and textures will be remove because this would otherweise
     * lead to data objects >100MB.
     * @returns {string} - ccAO build instructions as JSON string.
     */
    getBuildInstructions() {
        let buildInstruction = new this.hcs.cad.ccAO.BuildInstruction({ version: 2});
        buildInstruction.readInstancesFromScene();

        return buildInstruction.exportJSON({
            whiteList: [
                {
                    property: "appearance", whiteList: [
                        { property: "anchor" },
                        { property: "buttons" },
                        { property: "color" },
                        { property: "visible" },
                        { property: "status" }
                    ]
                },
                { property: "custom" },
                { property: "id" },
                { property: "loader" },
                { property: "name" },
                {
                    property: "placement", whiteList: [
                        { property: "replace" },
                        { property: "source" },
                        { property: "target" },
                        { property: "transformations" }
                    ]
                },
                {
                    property: "product", whiteList: [
                        { property: "booleanOp" },
                        { property: "model" },
                        { property: "nLang" },
                        { property: "parameters" },
                        { property: "parameterListOrder" },
                        { property: "type" }
                    ]
                },
                {
                    property: "properties", whiteList: [
                        { property: "anchorsToSensitivObjects" },
                        { property: "instanceToSensitivObjects" },
                        { property: "receiveClickEvent" }
                    ]
                }
            ]
        });
    }

    /**
     * Private function which handeles data in local or session storage.
     * @param {string} target - Either "local" or "session".
     * @param {string} key - Storage key.
     * @param {string} [storageValue] - Value to be saved in storage.
     * Returns current value in storage when this parameter is not defined.
     * When calling with "reset" the complete data object will be set to initial values.
     * @returns {string} Current storage value after being set.
     */
    _storage(target, key, storageValue) {
        const storageTarget = target + "Storage";
        const storageKey = `hcs-storage-${this.hcs.configurator}-${key}`;

        // When browser does not support local storage
        // or user has disabled it we just return the standard object.
        // In this case it is not possible to restore abandoned sessions.
        if(!this.storageAvailable(storageTarget)) {
            console.warn(`HCS: Local storage '${storageTarget}' not available or disabled by user!`);

            return null;
        }

        switch(storageValue) {
            case "reset":
                window[storageTarget].removeItem(storageKey);
                return null;

            default: {
                if(!storageValue) {
                    return window[storageTarget].getItem(storageKey);
                }

                window[storageTarget].setItem(storageKey, storageValue);

                return storageValue;
            }
        }
    }

    /**
     * Read/write configuration data in local user session storage.
     * @param {object} [storageDataSet] - Object containing one or more entries which shall be added/set in current storage.
     * When calling with "reset" the complete data object will be set to initial values.
     * With the "debug-info" option it'll return a data object which can be used for debugging in debug window console
     * using the "table" function.
     * @returns {object} Current data object in storage. When new values have been set with "storageDataSet"
     * the result object contains also the new data.
     */
    configStorage(storageDataSet) {
        const storageKey = "configuration";
        const storageTarget = "local";

        if(storageDataSet === "debug-info") {
            const storData = this.configStorage();

            return Object.keys(storData).map(key => {
                let dat = {};
                let value = "-";

                switch(key) {
                    case "ccAOData":
                        if(storData[key] && storData[key].instances) {
                            value = "Num. instances: " + storData[key].instances.length;
                        }
                        break;

                    default:
                        value = storData[key] || "-";
                        break;
                }

                dat[key + ":"] = value;

                return dat;
            });
        }

        // When browser does not support local storage
        // or user has disabled it we just return the standard object.
        // In this case it is not possible to restore abandoned sessions.
        if(!this.storageAvailable(storageTarget + "Storage")) {
            console.warn(`HCS: Local storage '${storageTarget}' not available or disabled by user!`);

            return {
                configurator: this.hcs.configurator,
                configurationId: this.hcs.configurationId,
                configured: false,
                ccAOData: null
            };
        }

        switch(storageDataSet) {
            case "reset":
                this.storage[storageTarget](storageKey, "reset");
                break;

            default:
                if(!this.storage[storageTarget](storageKey)) {
                    this.storage[storageTarget](storageKey, JSON.stringify({
                        configurator: this.hcs.configurator,
                        configurationId: this.hcs.configurationId,
                        configured: false,
                        ccAOData: null
                    }));
                }

                let storageObject = JSON.parse(this.storage[storageTarget](storageKey));

                if(arguments.length < 1) {
                    return storageObject;
                }

                if(!storageDataSet) {
                    console.error("'storageDataSet' value for configStorage() cannot be null!");
                    return;
                }

                storageObject = objectAssignDeep({}, storageObject, storageDataSet);

                this.storage[storageTarget](storageKey, JSON.stringify(storageObject));
                return storageObject;
        }
    }

    /**
     * Access to different storage endpoints.
     * @param {string} key - Storage key.
     * @param {string} [storageData] - Value to be stored.
     * Returns current value in storage when this parameter is not defined.
     * When calling with "reset" the complete data object will be set to initial values.
     * @returns {string} Current data object in storage. When new values have been set with "storageDataSet"
     * the result object contains also the new data.
     */
    get storage() {
        return {
            local: (...args) => this._storage.apply(this, ["local"].concat(args)),
            session: (...args) => this._storage.apply(this, ["session"].concat(args))
        };
    }

    /**
     * Check of local storage is available in current browser.
     * @param {string} type - Type of storage to be checked.
     * @returns {boolean} Return true when storage is supported by browser.
     */
    storageAvailable(type) {
        let storage;

        try {
            storage = window[type];
            let x = "__storage_test__";
            storage.setItem(x, x);
            storage.removeItem(x);
            return true;
        }
        catch(e) {
            return e instanceof DOMException && (
                // everything except Firefox
                e.code === 22 ||
                // Firefox
                e.code === 1014 ||
                // test name field too, because code might not be present
                // everything except Firefox
                e.name === "QuotaExceededError" ||
                // Firefox
                e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
                // acknowledge QuotaExceededError only if there's something already stored
                (storage && storage.length !== 0);
        }
    }

    /**
     * Save current configuration.
     * @param {object} [options] - Options
     * @param {bool} [options.showConfirmDialog=true] When set to "true" or not specified a dialog is being displayed when configuration has been saved.
     * @param {bool} [options.createShortLink=false] When true we'll create a short link ID which can be used for sharing this configuration.
     * @param {string} [options.configurationName] When not specified a dialog is being shown where user can enter a configuration name.
     * @param {bool} [options.sendToShop=true] When "true" this configuration will be send to web shop.
     * @param {bool} [options.saveAsCopy=false] When "true" it'll create a copy of the current configuration under a new configuration ID.
     * @param {function} [options.success] Function being called after configuraiton has been saved.
     * When option is set this value will be used as configuration name without showing a dialog.
     */
    saveConfiguration(options) {
        let self = this;
        
        let opts = Object.assign({}, {
            checkAuthentication: false,
            showConfirmDialog: true,
            createShortLink: false,
            configurationName: null,
            sendToShop: true,
            saveAsCopy: false,
            success: function() { }
        }, options);

        if( opts.checkAuthentication && !this.checkAuthentication() ) { return; }
        if( !this.checkFunction("configSave", opts.checkAuthentication, "WarningSaveLocked") ) { return; }


        var txControl = null;
        var txId = "tx" + new Date().getTime();
        var dialogError = null;

        var saveConfig = function(saveOptions) {
            var saveOpts = Object.assign({}, {
                configDescription: "",
                newConfigurationId: null,
                loadNewConfiguration: false
            }, saveOptions);

            self.messageBox.close();

            let loading = true;

            setTimeout(() => {
                if(loading) {
                    self.progressBar.progress("indeterminate");
                }
            }, 200);

            self.callback({
                url: self.ecs({
                    method: "saveConfiguration", params: {
                        configdescription: saveOpts.configDescription,
                        newConfigurationId: saveOpts.newConfigurationId,
                        share: opts.createShortLink ? "1" : "0"
                    }
                }),
                data: {
                    "ccAOData": JSON.stringify(self.getBuildInstructions())
                },
                method: "POST",
                checkSuccess: function(result) { return result.success; }
            }).then(result => {
                loading = false;
                self.progressBar.progress(false);

                if(result.success) {
                    // We store the current configuration description as global API property
                    // so that the "SETTINGS.SHOP_CONFIGURATION_SAVE" shop function
                    // can access it via: window.cadclick.hcs['$$CFG_NAME$$'].api.configDescription
                    self.configDescription = saveOpts.configDescription;

                    if(opts.sendToShop) {
                        self.hcs.externalFunction("SETTINGS.SHOP_CONFIGURATION_SAVE").then(shopResult => {
                            if(typeof (shopResult) !== "undefined" && shopResult !== null && shopResult.uuid) {
                                self.callback({
                                    url: self.ecs({ method: "updateConfiguration" }),
                                    data: {
                                        "shopId": shopResult.uuid
                                    },
                                    method: "POST",
                                    checkSuccess: function(result) { return result.success; }
                                }).then(result => {
                                    if(opts.showConfirmDialog) {
                                        self.messageBox.show({
                                            title: self.hcs.t.gui("SaveConfiguration_Dialog_Title"),
                                            text: self.hcs.t.gui("SaveConfiguration_Dialog_Message"),
                                            messageType: MessageBox.MB_INFO,
                                            buttons: MessageBox.MB_OK
                                        });
                                    }

                                    result.configurationId = saveOpts.newConfigurationId || self.hcs.configurationId;
                                    opts.success.call(self, result);
                                });
                            } else {
                                self.showError({
                                    title: self.hcs.t.gui("SaveConfiguration_Dialog_Title"),
                                    message: {
                                        message: self.hcs.t.gui("SaveConfiguration_ShopNotLogin") + "<br /><br />Shop result:<br />" + JSON.stringify(shopResult)
                                    }
                                });
                            }
                        }).catch(e => {
                            loading = false;

                            self.showError({
                                title: "Could not save configuration",
                                message: e,
                                messageMap: "jsError"
                            });
                        });
                    } else {
                        result.configurationId = saveOpts.newConfigurationId || self.hcs.configurationId;
                        opts.success.call(self, result);
                    }
                } else {
                    self.showError({
                        title: self.hcs.t.gui("SaveConfiguration_Dialog_Title"),
                        message: result.error
                    });
                }
            });
        };

        var checkConfigAndSave = function(configurationDescription) {
            if(configurationDescription === "") {
                dialogError.style.visibility = "visible";
            } else {
                if(dialogError) {
                    dialogError.style.visibility = "hidden";
                }

                saveConfig({ configDescription: configurationDescription });
            }
        };

        switch(true) {
            case opts.saveAsCopy:
                self.createConfigurationId().then(newUid => {
                    saveConfig({
                        newConfigurationId: newUid,
                        configDescription: options.configurationName,
                        loadNewConfiguration: true
                    });
                });
                break;

            case opts.configurationName === null:
                this.messageBox.show({
                    title: this.hcs.t.gui("SaveConfiguration_Dialog_Title"),
                    text: "<div class='hcs-save-config-dlg'><label>" + this.hcs.t.gui("SaveConfiguration_Dialog_ConfigName") + "</label><input id='" + txId + "' type='text' class='hcs-textbox' maxlength='255' /><span class='hcs-error'>" + this.hcs.t.gui("SaveConfiguration_Dialog_ConfigNameError") + "</span></div>",
                    buttons: MessageBox.MB_OK | MessageBox.MB_CANCEL,
                    messageType: MessageBox.MB_INFO,
                    onOk: function() {
                        checkConfigAndSave(txControl.value.trim());
                    },
                    onOpen: function() {
                        dialogError = document.querySelector(".hcs .hcs-save-config-dlg .hcs-error");
                        txControl = document.getElementById(txId);

                        txControl.focus();
                        txControl.addEventListener("keyup", (event) => {
                            if(event.key == "Enter") {
                                checkConfigAndSave(txControl.value.trim());
                            }
                        });
                    }
                });
                break;

            default:
                checkConfigAndSave(opts.configurationName);
                break;
        }
    }

    /**
     * Share current configuration by creating a short link.
     */
    shareConfiguration() {
        let self = this;

        if(!this.checkFunction("configShare")) { return; }

        this.hcs.api.logToolbarOption("Share Configuration");

        this.saveConfiguration({
            showConfirmDialog: false,
            createShortLink: true,
            sendToShop: false,
            configurationName: `share_${this.hcs.configurationId}`,
            success: function(result) {
                let urlPlain = window.location.href.split("?")[0];
                let urlParams = {};

                urlParams["hcs-load-configuration"] = result.shortId;
                urlParams["hcs-configuration-id-type"] = "Short";

                let urlParamNames = Object.keys(urlParams);
                let shareUrl = urlPlain + "?" + urlParamNames.map((param) => { return param + "=" + urlParams[param]; }).join("&");

                let shareContainer = self.instance(".hcs-share");
                let shareLinkContainer = shareContainer.querySelector(".hcs-share-link");
                let overlayContainer = self.instance(".hcs-background-main");

                // Remove all links to initialize with new click event listeners.
                shareLinkContainer.innerHTML = "";

                let arQr = qrCode(0, "L");
                arQr.addData(shareUrl);
                arQr.make();

                shareContainer.querySelector(".hcs-close").addEventListener("click", () => {
                    shareContainer.classList.remove("hcs-show");
                    overlayContainer.classList.remove("hcs-show");
                    shareContainer.querySelector(".hcs-content").innerHTML = "";
                }, { once: true });

                shareContainer.classList.add("hcs-show");
                overlayContainer.classList.add("hcs-show");

                shareContainer.querySelector(".hcs-content").innerHTML = arQr.createSvgTag();
                shareContainer.querySelector(".hcs-content svg").removeAttribute("width");
                shareContainer.querySelector(".hcs-content svg").removeAttribute("height");

                let shareCopyLinkValue = document.createElement("textarea");
                shareCopyLinkValue.value = shareUrl;

                let shareCopyLink = document.createElement("a");
                shareCopyLink.className = "hcs-share-copy";
                shareCopyLink.innerHTML = `<svg icon-id="icn-036-2-send" version="2.0">
                        <use href="#icn-036-2-send"></use>
                    </svg>` + self.hcs.t.gui("ShareLinkCopy");

                shareCopyLink.addEventListener("click", () => {
                    shareCopyLinkValue.select();
                    navigator.clipboard.writeText(shareCopyLinkValue.value);

                    shareCopyLink.classList.add("hcs-hidden");
                    shareCopyLinkSuccess.classList.remove("hcs-hidden");

                    setTimeout(() => {
                        shareCopyLink.classList.remove("hcs-hidden");
                        shareCopyLinkSuccess.classList.add("hcs-hidden");
                    }, 2000);
                });

                let shareCopyLinkSuccess = document.createElement("span");
                shareCopyLinkSuccess.className = "hcs-share-copy-success hcs-hidden";
                shareCopyLinkSuccess.innerHTML = `<svg icon-id="icn-075-2-tick-bold" version="2.0">
                        <use href="#icn-075-2-tick-bold"></use>
                    </svg>` + self.hcs.t.gui("CopyToClipboardShareSuccess");

                let shareMailLink = document.createElement("a");
                shareMailLink.className = "hcs-share-mail";
                shareMailLink.innerHTML = `<svg icon-id="icn-023-2-email" version="2.0">
                        <use href="#icn-023-2-email"></use>
                    </svg>` + self.hcs.t.gui("ShareLinkMail");
                shareMailLink.href = `mailto:?subject=${encodeURIComponent(self.hcs.t.gui("ShareLinkMail_Subject"))}&body=${encodeURIComponent(shareUrl)}`;

                shareLinkContainer.appendChild(shareCopyLink);
                shareLinkContainer.appendChild(shareCopyLinkSuccess);
                shareLinkContainer.appendChild(shareCopyLinkValue);
                shareLinkContainer.appendChild(shareMailLink);

                self.hcs.tracker.track(TrackingTypes.ShareLink, { "url": shareUrl });
            }
        });
    }

    /**
     * Load an existing configuration.
     * @param {any} configurationId - ID of the configuration to be loaded.
     * @param {boolean} [fromLocalStorage=false] - Set true to load ccAO data from local storage-
     * Otherwise ccAO data will be loaded from configuration state in database.
     * @returns {Promise} Promise object being triggered when loading configuration has finished.
     */
    loadConfiguration(configurationId, fromLocalStorage = false) {
        let self = this;

        self.hcs.configurationId = configurationId;

        // We convert the (external) configuration ID to a UID which will be accepted by ccGenerator through ccAO.
        self.hcs.cad.ccAO.Options.sessionId = uuid();
        self.hcs.cad.ccAO.Options.configId = self.hcs.cad.ccAO.Options.sessionId;

        this.hcs.renderer.reset();
        this.hcs.cart.setPrice(0);

        let loading = true;

        setTimeout(() => {
            if(loading) {
                self.progressBar.progress("indeterminate");
            }
        }, 200);

        return new Promise((resolve) => {
            this.hcs.renderer.configure("loadConfiguration", null, null, {
                fromLocalStorage: fromLocalStorage,
                success: function() {
                    loading = false;
                    self.progressBar.progress(false);

                    // Loaded configurations don't show the rating control anymore.
                    self.ratingControl.sessionState(true);

                    self.configStorage({
                        configurationId: configurationId,
                        configured: true
                    });

                    self.updateHeartbeat(configurationId);

                    resolve();
                }
            });
        });
    }

    /**
     * Load configuration data from database.
     * @param {string} configurationId - ID of the configuration to be loaded.
     * @param {boolean} fromLocalStorage - Set "true" to load pending configurations,
     * "false" to load saved or shared configurations.
     * @param {function} success - Callback when request has finished. The response object
     * contains the complete database entry.
     */
    loadConfigurationObject(configurationId, fromLocalStorage, success) {
        this.callback({
            url: this.getUrl(`/configuration/get/${configurationId}/${fromLocalStorage}`),
            method: "GET"
        }).then(result => {
            success(result);
        });
    }

    /**
     * Check if configuration with given ID exists an database.
     * @param {string} configurationId - ID of the configuration to be checked.
     * @param {function} success - Callback when request has finished. The response object
     * contains only a sinlge property "exists", with either "true" or "false.
     */
    checkConfigurationExists(configurationId, success) {
        this.callback({
            url: this.getUrl(`/configuration/exists/${configurationId}`),
            method: "GET"
        }).then(result => {
            success(result);
        });
    }

    /**
     * Start file download of given URL.
     * @param {string} url - Absolute URL of the download.
     * @param {object} [options] - Additional options
     * @param {boolean} [options.forceDownload=true] - Force file download to override browser-specific display modes
     * @param {string} [options.target] - Set "target" attribute like "_blank", ...
     * (like inline PDF viewer).
     */
    downloadFile(url, options) {
        if( !this.checkAuthentication() ) { return; }
        if( !this.checkFunction("downloadFiles", true, "WarningDownloadLocked") ) { return; }

        let opts = Object.assign({}, {
            forceDownload: true,
            target: "_blank",
            filename: ""
        }, options);

        let logParams = [];
        let logParam = { fileName: opts.filename };
        logParams.push(logParam);
        this.logToolbarOption("Download File", logParams);
        
        let element = document.createElement("a");
        element.setAttribute("href", url);

        if(opts.forceDownload) {
            element.setAttribute("download", opts.filename);
        }

        if(opts.target) {
            element.setAttribute("target", opts.target);
        }

        element.style.display = "none";
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    }

    logToolbarOption(optionId, params = []) {
        this.callback({
            url: this.getUrl(`/log/toolbar/${this.hcs.configurationId}/${optionId}`)
        });

        let data = { "optionId": optionId }
        if( params ) {
            params.forEach(item => Object.assign(data, item));
        }

        let res = {};
        Object.keys(data).forEach(key => {
            if(data[key]) { res[key] = data[key]; }
        });

        this.hcs.tracker.track(TrackingTypes.Toolbar, res);
    }

    /**
     * Start AR mode.
     * Depending on device we:
     * - desktop: Open a dialog an show a QR code which has to be scanned on the mobile device
     * - mobile/tablet: Immediately start the AR app on the mobile device itself.
     * @returns {bool} "false" in case AR is not allowed to start.
     */
    startAr() {
        let self = this;

        if( !this.checkFunction("configSave") ) { return; }

        this.logToolbarOption("AR");

        let arUrl = this.getUrl(`/ar/${this.hcs.configurationId}`);

        let arCodeContainer = this.instance(".hcs-arcode");
        let overlayContainer = this.instance(".hcs-background-main");

        let windowReference = null;

        // Special app start for iOS.
        // This is required because Apple does not allow the "downloadFile" function within async functions.
        // "this.saveCustomOption" below is async in our case.
        // The workaround is this:
        //
        // let wndRef = window.open();
        //
        // async() {
        //    wndRef.location = "https://.....";
        // };
        if(this.hcs.ui.browser.os.name === "iOS" && ["mobile", "tablet"].includes(this.hcs.ui.browser.platform.type)) {
            windowReference = window.open();
        }

        let arStart = function() {
            let arAppLinkCfg = JSON.parse(self.hcs.settings.get("SETTINGS.AR_APP_LINK"));

            switch(this.hcs.ui.browser.platform.type) {
                case "mobile":
                case "tablet": {
                        if(!arAppLinkCfg[this.hcs.ui.browser.os.name]) {
                            self.showError({
                                title: "Could not start AR app",
                                message: `'AR_APP_LINK' property does not contain a value for platform/os: ${this.hcs.ui.browser.platform.type}/${self.hcs.ui.browser.os.name}`
                            });
                        } else {
                            let arAppLink = urlJoin(arAppLinkCfg[this.hcs.ui.browser.os.name], "app", this.hcs.ui.browser.platform.type, this.hcs.ui.browser.os.name) + "?model=" + encodeURIComponent(arUrl);

                            switch(this.hcs.ui.browser.os.name) {
                                case "iOS":
                                    windowReference.location = arAppLink;
                                    break;

                                default:
                                    this.downloadFile(arAppLink, { forceDownload: true, target: "_blank" });
                                    break;
                            }
                        }
                    }
                    break;

                default: {
                    const camOs = "Camera";
                    let arQr = qrCode(0, "L");

                    if(!arAppLinkCfg[camOs]) {
                        self.showError({
                            title: "Could not start AR app",
                            message: `'AR_APP_LINK' property does not contain a value for platform/os: ${this.hcs.ui.browser.platform.type}/${camOs}`
                        });
                    } else {
                        let arAppLink = urlJoin(arAppLinkCfg[camOs], "app") + "?model=" + encodeURIComponent(arUrl);

                        arQr.addData(arAppLink);
                        arQr.make();

                        // Add class which detects current browser platform (desktop, mobile, table).
                        arCodeContainer.classList.add("hcs-platform-" + this.hcs.ui.browser.platform.type);

                        arCodeContainer.querySelector(".hcs-close").addEventListener("click", () => {
                            arCodeContainer.classList.remove("hcs-show");
                            overlayContainer.classList.remove("hcs-show");
                            this.instance(".hcs-arcode .hcs-content").innerHTML = "";
                        }, { once: true });

                        arCodeContainer.classList.add("hcs-show");
                        overlayContainer.classList.add("hcs-show");

                        this.instance(".hcs-arcode .hcs-content").innerHTML = arQr.createSvgTag();
                        this.instance(".hcs-arcode .hcs-content svg").removeAttribute("width");
                        this.instance(".hcs-arcode .hcs-content svg").removeAttribute("height");
                    }
                }
                break;
            }
        };

        arStart.call(self);
    }

    /**
     * Show rating control.
     * This also ensures that duplicate rating are avoided.
     * Therefore we check the current session state for already completed ratings.
     * @param {object} [options] - Additional call options
     * @param {object} [options.force=false] - When "true" open rating control without check of previous user ratings (per session).
     * @returns {bool} "true" when reating controls opens. "false" when rating for this configuration has
     * already been done and rating dialog stays hidden.
     */
    startRating(options) {
        var opts = Object.assign({}, {
            force: false
        }, options);

        return this.ratingControl.show(opts);
    }

    /**
     * Some functions in configurator are not always available from the beginning.
     * This functions checks if the given function (identified by unique name) can be accessed.
     * Each configurator class must override the {@link ConfiguratorBase#functionCheckList}) function
     * and return a list of checks.
     * @param {string} functionName - Name of the function to check.
     * @param {boolean} [showWarningDialog=true] - Show warning dialog when function is currentyly disabled.
     * @returns {boolean} true when given function can be executed,
     * false when function is not yet available or the configurator class does not return an appropriate check function.
     */
    checkFunction(functionName, showWarningDialog = true, warningMessageId = "WarningFunctionLocked") {
        let checkFunction = this.hcs.cfgEngine.functionCheckList().find(i => { return Object.keys(i)[0] === functionName; });

        let checkOk = (typeof (checkFunction) !== "undefined" && checkFunction[functionName].call());

        if(!checkOk && showWarningDialog) {
            this.messageBox.show({
                modal: true,
                title: "",
                text: this.hcs.t.gui(warningMessageId),
                buttons: MessageBox.MB_OK,
                messageType: MessageBox.MB_INFO
            });
        }

        return checkOk;
    }

    checkAuthentication(showWarningDialog = true) {
        if( !this.hcs.isAuthenticated && showWarningDialog ) {
            this.messageBox.show({
                modal: true,
                title: "",
                text: this.hcs.t.gui("WarningNotAuthenticated"),
                buttons: MessageBox.MB_OK,
                messageType: MessageBox.MB_INFO
            });
        }

        return this.hcs.isAuthenticated;
    }

    /**
     * Create unique ID which can be used like for HTML controls IDs.
     * @param {string} prefix - Custom prefix text which will be added in front
     * of the unique ID.
     * @returns {string} Unique ID with given prefix
     */
    createUniqueId(prefix) {
        return prefix + "-" + Math.random().toString(36).slice(2, 11) + "-" + Math.random().toString(36).slice(2, 11);
    }

    /**
     * Returns AJAX body compression.
     * @returns {bool} "true" When AJAX body compressing is enabled to POST data back to server.
     */
    get ajaxBodyCompressionEnabled() {
        return this.hcs.settings.get("SETTINGS.AJAX_COMPRESS_BODY", "bool") && window.cadclick.loader.getUrlParameterByName("hcs-ajax-compress-body") !== "0";
    }

    /**
     * Automatically create compressed or uncompressed POST body data depending on enabled AJAX data compression.
     * @param {any} data - String or JSON object to compress.
     * @param {bool} [stringify=false] - When data compression is disabled you can choose wether the original JSON data will be
     * stringified or not.
     * @returns {string} Base&4 compressed data.
     */
    ajaxCompressBody(data, stringify = false) {
        let dataToCompress = typeof data === "string" ? data : JSON.stringify(data);

        return this.ajaxBodyCompressionEnabled ? LZString.compressToBase64(dataToCompress) : (stringify ? JSON.stringify(data) : data);
    }

    /**
     * Check given string value if it is null er an empty string.
     * If so return a replacement value otherwise the original value.
     * @param {string} value - Original value to check if empty (or null).
     * @param {string} replace - Replacement value being returns when value is empty.
     * @returns {string} Original value when not empty, otherwise the replace value.
     */
    emptyReplace(value, replace) {
        return value !== null && value !== "" ? value : replace;
    }

    /**
     * Round a (decimal) number by a given number of digits after comma.
     * @param {number} number - Decimal number to be rounded.
     * @param {number} decimals Number of digits after comma.
     * @returns {number} - Rounded number.
     */
    round(number, decimals) {
        return Math.round(number * Math.pow(10, decimals)) / Math.pow(10, decimals);
    }

    /**
     * Resolve "path" property of event objects.
     * The "path" property is only supported by Chrome browsers.
     * This contains an array of all items belonging to the triggered event in hierarchical order
     * beginning with the target element ending with document (or window) element.
     * This function creates this array when not supported by browser.
     * @param {event} event - Event object.
     * @returns {Array} Array of DOM elements.
     */
    eventPath(event) {
        if(event.composedPath) {
            return event.composedPath();
        }

        let items = [event.target];

        let parent = event.target;

        while((parent = parent.parentElement)) {
            items[items.length] = parent;
        }

        return items;
    }

    /**
     * Create screenshot from given DOM element inside HCS instance.
     * @param {string|HTMLElement} [selector] - DOM selector or DOM element.
     * Leave empty (or undefined) to capture the entire HCS container.
     * @param {any} [options] - Additional options for html2canvas.
     * @see {@link https://html2canvas.hertzen.com/configuration} for all options.
     * @returns {Promise} Promise object being triggered when capture has finished.
     * It returns the PNG image data as URI.
     */
    screenshot(selector, options) {
        let opts = Object.assign({}, {
            backgroundColor: null,
            dpi: 600,
            scale: 2,
            imageType: "image/png"
        }, options);

        return new Promise(resolve => {
            html2canvas(typeof (selector) === "string" ? this.instance(selector) : selector, opts).then(canvas => {
                let myImage = canvas.toDataURL(opts.imageType);
                resolve(myImage);
            });
        });
    }

    /**
     * Resize given image (as Base64 URL).
     * @param {string} url - Base64 URL of source image to be scaled.
     * @param {int} width - Desired image width
     * @param {int} height - Desired image height
     * @returns {Promise} Promise object which calles "resolve" when image has been finally scaled.
     * The function argument contains the new Base64 URL of the scales image.
     */
    resizeImage(url, width, height) {
        var sourceImage = new Image();

        return new Promise(resolve => {
            sourceImage.onload = function() {
                // Create a canvas with the desired dimensions
                var canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;

                // Scale and draw the source image to the canvas
                canvas.getContext("2d").drawImage(sourceImage, 0, 0, width, height);

                // Convert the canvas to a data URL in PNG format
                resolve(canvas.toDataURL());
            };

            sourceImage.src = url;
        });
    }

    /**
     * Parse JSON string.
     * In case of an error the message contains the erroneous parst of the JSON string
     * with a marker indicating the exact position of the error.
     * When JSON contains an error the result could look like this:
     * 
     * Error parsing JSON position 128:
     * ..."param1": "value1", "param2": , "param3": "value3"...
     * --------------------------------^
     * 
     * We extract the JSON part which contains the error and draw a marker line with arrow below
     * to point to the exact error position.
     * @param {string} jsonString - JSON content as string.
     * @param {object} [options] - Options
     * @param {string} [options.errorMessage] - Additional error message which will be added at the beginning
     * of the message (in case of an error).
     * @param {bool} [options.writeJsonToConsole=true] - When enabled the complete JSON code will be written
     * to browser console in case of an error.
     * @returns {object} Given JSON string converted to object.
     */
    jsonParse(jsonString, options) {
        var opts = Object.assign({}, {
            errorMessage: null,
            writeJsonToConsole: true
        }, options);

        try {
            return JSON.parse(jsonString);
        } catch(e) {
            // Write entire JSON to browser console.
            if(opts.writeJsonToConsole) {
                console.error(jsonString);
            }

            var errorMessage = (opts.errorMessage ? opts.errorMessage : "") + e.message;

            // Parse the original error message for a string like "..... position 248..."
            // We try to extract the position of the error.
            var posMatch = /(position )([0-9]+)/.exec(e.message);
            var jsonErrorPos = -1;

            if(posMatch && posMatch.length > 2) {
                jsonErrorPos = parseInt(posMatch[2]);

                if(isNaN(jsonErrorPos)) {
                    jsonErrorPos = -1;
                }
            }

            // When we could find the error position we show the JSON snipped which contains the error
            // and we show an arrow pointing to the exact position.
            if(jsonErrorPos > -1) {
                var startIndex = Math.max(0, jsonErrorPos - 20);
                var endIndex = Math.min(jsonString.length - 1, jsonErrorPos + 20);
                errorMessage += "\n<code>..." + jsonString.substring(startIndex, endIndex) + "...\n" +
                    "-".repeat(jsonErrorPos - startIndex - 1 + 3) + "^\n</code>";
            }

            throw new Error(errorMessage);
        }
    }

    /**
     * Read or store user session data values.
     * @param {string} key - Session key
     * @param {object} [value] - When defined set this value for the given key. When not defined the current
     * session value will be returned.
     * @returns {object} When no value is specified the current session value for the given key is returned.
     */
    session(key, value) {
        if(arguments.length < 2) {
            return this._sessionData[key];
        }

        this._sessionData[key] = value;
    }

    /**
     * Wait for HTML DOM images to be completely loaded.
     * @param {any} images - List of DOM image elements, query selector, a single image DOM element
     * or an array containing DOM image elements.
     * @param {object} [options] - Additional options
     * @param {number} [options.timeout=5000] - Timeout in milliseconds when image completion check will be cancelled.
     * @returns {Promise} Promise object which resolve after all images have been loaded or the given retry count has exceeded.
     */
    waitImagesLoaded(images, options) {
        options = Object.assign({}, {
            timeout: 5000
        }, options);

        const startRetryCount = 10;

        switch(true) {
            // Convert single DOM item to array
            case typeof images === "object" && (images.tagName || "").toUpperCase() === "IMG":
                return this.waitImagesLoaded([images]);

            // Convert NodeList (result of querySelectorAll()) to array
            case typeof images === "object" && typeof images.length === "number" && typeof images.filter === "undefined":
                return this.waitImagesLoaded([...images]);

            // Convert DOM selector to array
            case typeof images === "string":
                return this.waitImagesLoaded(document.querySelectorAll(images));
        }

        // Only check IMG tags in case of given list contains other elements than IMG.
        images = images.filter(image => (image.tagName || "").toUpperCase() === "IMG");

        return new Promise((resolve, reject) => {
            let imageRetryCount = startRetryCount;

            try {
                var checkImages = _ => {
                    // Image is "complete" when either successfully loaded or image URL does not exist.
                    let lstPending = images.filter(image => !image.complete);

                    if(lstPending.length && imageRetryCount > 0) {
                        imageRetryCount--;

                        setTimeout(_ => {
                            checkImages();
                        }, Math.max(1, options.timeout / startRetryCount));
                    } else {
                        resolve(images);
                    }
                };

                checkImages();
            } catch(e) {
                reject(e);
            }
        });
    }

    /**
     * Created Base64 encoded images.
     * Send all given URLs to image proxy, which reads all URLs
     * and returns an array with all images encoded as Base64.
     * @param {string|array} imageUrls - Single URL or list of image URLs
     * @returns {Promise} Contains object where property name equals to image URL.
     * This allows easy reading of belonging Base64 data to given URL. The content
     * is then an object with image info and the Base64 string itself.
     * The result object is as follows:
     * 
     * {
     *     Items: [
     *         {
     *             data: "/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw....",
     *             url: "http://localhost/HAEFELE_CONFIGURATIONSUITE_DE/dress_code/ConfigImage/icons/thumbs/80330500.jpg",
     *             mimeType: "image/jpeg",
     *             success: true,
     *             error: null,
     *             width: 120,
     *             height: 120
     *         },
     *         ...
     *     ]
     * }
     * 
     * The data string can then be directly used for example as "src" parameter for image elements.
     * Only requires the "data:image/jpeg;base64," prefix. Ensure to use the correct mime type here.
     * This value is also available in the response.
     */
    imageUrlToBase64(imageUrls) {
        if(typeof imageUrls === "string") {
            imageUrls = [imageUrls ];
        }

        return new Promise(resolve => {
            this.hcs.api.callback({
                url: this.hcs.api.getUrl("/image2base64"),
                method: "POST",
                data: {
                    items: imageUrls
                }
            }).then(result => {
                let imageMap = {};

                result.items.forEach(item => {
                    imageMap[item.url] = item;
                });

                resolve(imageMap);
            });
        });
    }

    /**
     * Return enumeration name of given value.
     * Example:
     * @example
     * Given enum:
     * 
     * let myEnum = {
     *     item1: 0,
     *     item2: 1,
     *     item3: 2
     * }
     * 
     * enumValue(myEnum, 1)
     * 
     * Retruns: "item2"
     * @param {object} enumObject Complete enum object.
     * @param {any} value - Value to be resolved
     * @returns {string} Name of the belonging enum value.
     */
    enumValue(enumObject, value) {
        return Object.keys(enumObject).find(key => enumObject[key] === value);
    }

    /**
     * Converts rgba() CSS value to HTML color code.
     * 
     * @param {string} colorValue - rgba() color value like: rgba(255, 128, 23, 0);
     * @retuns {string} Value converted to HTML color code: #ff8017
     */
    rgbaToColorCode(colorValue) {
        let parts = colorValue.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);

        delete (parts[0]);

        for(var i = 1; i <= 3; ++i) {
            parts[i] = parseInt(parts[i]).toString(16);
            if(parts[i].length == 1) parts[i] = "0" + parts[i];
        }

        return "#" + parts.join("");
    }

    /**
     * Converts a Haefele product ID with no dots into an ID with dots.
     * @example
     * A value of 55764413 becomes 557.644.13
     * @param {string} productId - Product ID without any dots. When give value already contains a least
     * one single dot no formatting will be done.
     * @returns {string} Given product ID with dots.
     */
    formatProductId(productId) {
        let result = (productId || "").trim();

        if(result.length !== 8) {
            console.warn(`Could not format product ID because its required length is only ${result.length} digits instead of 8`);
            return result;
        }

        if(result.indexOf(".") < 0) {
            result = result.slice(0, 3) + "." + result.slice(3, 6) + "." + result.slice(6, 8);
        }

        return result;
    }

    /**
     * Opens image view popup.
     * @param {DOMElement} contentDom - DOM element being added to image view.
     * @param {object} [options] - Additional options
     * @param {string} [options.className] - Optional class name being added to view for additional styling.
     */
    imageView(contentDom, options) {
        options = Object.assign({}, {
            className: null
        }, options);

        var ivItem = this.instance(".hcs-image-view");

        let orgClass = ivItem.getAttribute("class");

        ivItem.innerHTML = "";
        ivItem.append(contentDom);

        if(options.className) {
            ivItem.classList.add(options.className);
        }

        ivItem.classList.add("hcs-visible");

        ivItem.addEventListener("click", _ => {
            ivItem.setAttribute("class", orgClass);
            ivItem.innerHTML = "";
        }, { once: true });
    }

    showMessageExpired(mode, expireTime) {
        let self = this;

        var dlgTitleAcr = "";
        var dlgText = "";
        var dlgButtonAcr = "";
        let sExpDisplayEl = this.instance(".hcs-session-expire-warn-time");

        switch(mode) {
            case "warn":
                if(sExpDisplayEl) {
                    sExpDisplayEl.innerHTML = expireTime;

                    if(this.instance(".hcs-session-expired-dialog").checkVisibility()) {
                        return;
                    }
                }

                if(this.showMessageExpiredMode === mode) {
                    return;
                }

                dlgTitleAcr = "SessionDialog_AskConfig_Title";
                dlgText = this.hcs.t.gui("SessionDialog_AskConfig_Text").replace("$TIME$", `<span class="hcs-session-expire-warn-time">${expireTime}</span>`);
                dlgButtonAcr = "SessionDialog_AskConfig_Button";
                break;

            case "expired":
                if(this.showMessageExpiredMode === mode) {
                    return;
                }

                dlgTitleAcr = "SessionDialog_Expired_Title";
                dlgText = this.hcs.t.gui("SessionDialog_Expired_Text");
                dlgButtonAcr = "SessionDialog_Expired_Button";
                break;

            default:
                console.error(`'${mode}' mode is not supported in 'showMessageExpired' function`);
                return;
        }

        this.showMessageExpiredMode = mode;

        this.messageBox.show({
            modal: true,
            title: this.hcs.t.gui(dlgTitleAcr),
            text: dlgText,
            closeOnEscape: false,
            titleCloseButton: false,
            classNames: "hcs-session-expired-dialog",
            buttons: MessageBox.MB_OK,
            buttonTexts: {
                "ok": this.hcs.t.gui(dlgButtonAcr)
            },
            buttonClassCategories: {
                "ok": "hcs-secondary"
            },
            messageType: MessageBox.MB_ERROR,
            onOk: function(event) {
                switch(mode) {
                    case "warn":
                        this.close();

                        self.callback({
                            url: self.ecs({ method: "heartbeat" }),
                            method: "GET"
                        }).then(_ => {
                            self.updateHeartbeat();
                            self.showMessageExpiredMode = null;
                        });
                        break;

                    case "expired":
                        this.close();
                        self.startConfiguration({ showConfirmDialog: false });
                        self.showMessageExpiredMode = null;
                        
                        break;
                }
            }
        });
    }

    /**
     * Add configuration change trigger.
     * @param {function} eventFunction Function being called after trigger change.
     */
    onConfigTrigger(eventFunction) {
        this.configTriggerEvents.push(eventFunction);
    }

    /**
     * Add callback trigger for ECS events.
     * @param {function} eventFunction Function being called after ECS callback.
     */
    onEcsTrigger(eventFunction) {
        this.ecsTriggerEvents.push(eventFunction);
    }

    /**
     * Add trigger when new configuration starts (user clicks "new configuration").
     * @param {function} eventFunction Function being called after new configuration has started.
     */
    onConfigurationReset(eventFunction) {
        this.configurationResetEvents.push(eventFunction);
    }

    /**
     * Add trigger when user switches between subconfigurations.
     * @param {function} eventFunction Function being called when subconfiguration changes.
     * The event function has two arguments:
     *   - subconfigurationId: Ident of the subconfiguration
     *   - subconfigurationIndex: Zero-based index of the subconfiguration 
     */
    onSubconfigurationChanged(eventFunction) {
        this.subconfigurationChangedEvents.push(eventFunction);
    }

    /**
     * Add trigger to catch print function being called in bottom navigation control.
     * @param {function} eventFunction Function being called when user clicks the "print" button.
     */
    onPrintTrigger(eventFunction) {
        this.printTriggerEvents.push(eventFunction);
    }
}